/* Cart Drawer — Shopify Ajax API
   Load with defer, after the cart drawer Liquid markup. */

const CartDrawer = (() => {
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = (path) => `${root}${path.replace(/^\//, '')}`;

  const SELECTORS = {
    drawer: '#cart-drawer',
    overlay: '#cart-drawer-overlay',
    closeBtn: '#cart-drawer-close',
    body: '#cart-drawer-body',
    itemsList: '#cart-drawer-items',
    emptyState: '#cart-drawer-empty-state',
    freeShipping: '#cart-drawer-free-shipping',
    footer: '#cart-drawer-footer',
    subtotal: '#cart-drawer-subtotal',
    checkoutBtn: '#cart-drawer-checkout',
    loader: '#cart-drawer-loader',
    errorBanner: '#cart-drawer-error',
    errorMsg: '#cart-drawer-error-msg',
    errorDismiss: '#cart-drawer-error-dismiss',
    status: '#cart-drawer-status',
    trigger: '#cart-drawer-trigger',
  };

  const el = {};
  let cart = null;
  let opener = null;
  let errorTimer = null;
  let requestController = null;
  let mutationVersion = 0;
  let isOpen = false;

  function getMoneyFormat() {
    return window.theme?.moneyFormat || window.Shopify?.money_format || '${{amount}}';
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, getMoneyFormat());
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en-ZA', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'ZAR',
    }).format(cents / 100);
  }

  function escapeHtml(value = '') {
    return String(value).replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    }[character]));
  }

  function getInlineIcon(name) {
    const template = document.getElementById(`cart-drawer-icon-${name}`);
    const icon = template?.content?.querySelector('svg')?.cloneNode(true);

    if (!icon) return '';

    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    icon.classList.add('cart-drawer__icon');

    return icon.outerHTML;
  }

  function setStatus(message = '') {
    if (el.status) {
      el.status.textContent = message;
    }
  }

  function setLoading(state) {
    el.loader?.setAttribute('aria-hidden', String(!state));
    el.itemsList?.setAttribute('aria-busy', String(state));
    el.drawer?.classList.toggle('cart-drawer--loading', state);

    if (state) {
      if (el.emptyState) {
        el.emptyState.hidden = true;
      }

      if (el.itemsList) {
        el.itemsList.hidden = false;
      }

      if (el.freeShipping) {
        el.freeShipping.hidden = true;
      }

      if (el.footer) {
        el.footer.hidden = true;
      }
    }
  }

  function setItemUpdating(item, state) {
    if (!item) return;

    item.classList.toggle('cart-drawer__item--updating', state);

    item.querySelectorAll('button').forEach((button) => {
      button.disabled = state;
    });
  }

  function updateCartBadge(count) {
    const hasItems = count > 0;
    const cartLabel = hasItems
      ? `Cart, ${count} ${count === 1 ? 'item' : 'items'}`
      : 'Cart';

    document
      .querySelectorAll('.header__cart-count, [data-cart-count]')
      .forEach((badge) => {
        badge.textContent = hasItems ? count : '';
        badge.hidden = !hasItems;
        badge.setAttribute('aria-hidden', 'true');
      });

    document
      .querySelectorAll('#cart-drawer-trigger, [data-cart-drawer-trigger]')
      .forEach((trigger) => {
        trigger.setAttribute('aria-label', cartLabel);

        const accessibleLabel = trigger.querySelector('.visually-hidden');

        if (accessibleLabel) {
          accessibleLabel.textContent = cartLabel;
        }
      });

    const headerCartStatus = document.getElementById('HeaderCartStatus');

    if (headerCartStatus) {
      headerCartStatus.textContent = hasItems
        ? `${count} ${count === 1 ? 'item' : 'items'} in your cart`
        : 'Your cart is empty';
    }
  }

  function showError(message = 'Something went wrong. Please try again.') {
    if (!el.errorBanner || !el.errorMsg) return;

    el.errorMsg.textContent = message;
    el.errorBanner.hidden = false;

    clearTimeout(errorTimer);
    errorTimer = window.setTimeout(dismissError, 6000);
  }

  function dismissError() {
    if (!el.errorBanner || !el.errorMsg) return;

    el.errorBanner.hidden = true;
    el.errorMsg.textContent = '';

    clearTimeout(errorTimer);
  }

  function showItemError(item, message) {
    if (!item) return;

    const messageElement = item.querySelector(
      '.cart-drawer__item-error-msg'
    );

    item.classList.add('cart-drawer__item--error');

    if (messageElement) {
      messageElement.textContent = message;
    }
  }

  function clearItemError(item) {
    if (!item) return;

    const messageElement = item.querySelector(
      '.cart-drawer__item-error-msg'
    );

    item.classList.remove('cart-drawer__item--error');

    if (messageElement) {
      messageElement.textContent = '';
    }
  }

  function updateFreeShipping(currentCart) {
    if (!currentCart) return;

    if (typeof window.updateFreeShippingProgress === 'function') {
      window.updateFreeShippingProgress(currentCart);
    }
  }

  function notifyCartUpdated(currentCart) {
    if (!currentCart) return;

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: currentCart,
        },
      })
    );
  }

  function updateEmptyState(currentCart) {
    const isEmpty = Number(currentCart.item_count || 0) === 0;

    if (el.emptyState) {
      el.emptyState.hidden = !isEmpty;
    }

    if (el.itemsList) {
      el.itemsList.hidden = isEmpty;
    }

    /*
      The free-shipping component appears only when the cart contains
      at least one item.
    */
    if (el.freeShipping) {
      el.freeShipping.hidden = isEmpty;
    }

    /*
      View Cart and Checkout are inside this footer, so hiding the footer
      removes both links automatically when the cart is empty.
    */
    if (el.footer) {
      el.footer.hidden = isEmpty;
    }

    if (el.subtotal) {
      el.subtotal.textContent = isEmpty
        ? formatMoney(0)
        : formatMoney(currentCart.total_price);
    }

    if (el.checkoutBtn) {
      el.checkoutBtn.setAttribute('aria-disabled', String(isEmpty));
      el.checkoutBtn.tabIndex = isEmpty ? -1 : 0;
    }
  }

  function renderProperties(properties) {
    if (!properties || typeof properties !== 'object') return '';

    const entries = Object.entries(properties).filter(([key, value]) => (
      key &&
      !key.startsWith('_') &&
      value != null &&
      value !== ''
    ));

    if (!entries.length) return '';

    return `
      <dl class="cart-drawer__item-properties">
        ${entries.map(([key, value]) => `
          <div class="cart-drawer__item-property">
            <dt>${escapeHtml(key)}:</dt>
            <dd>${escapeHtml(value)}</dd>
          </div>
        `).join('')}
      </dl>
    `;
  }

  function renderDiscounts(discounts) {
    if (!Array.isArray(discounts) || !discounts.length) return '';

    return `
      <ul class="cart-drawer__discounts" role="list">
        ${discounts.map((discount) => (
          `<li>${escapeHtml(discount.title)}</li>`
        )).join('')}
      </ul>
    `;
  }

  function itemMarkup(item) {
    const image = item.image || item.featured_image?.url || '';
    const productUrl = item.url || '#';
    const title = item.product_title || item.title || 'Product';
    const originalPrice = item.original_line_price ?? item.final_line_price;
    const hasDiscount = item.final_line_price < originalPrice;

    const minusIcon = getInlineIcon('minus');
    const plusIcon = getInlineIcon('plus');
    const removeIcon = getInlineIcon('remove');

    return `
      <li class="cart-drawer__item" data-key="${escapeHtml(item.key)}">
        <div class="cart-drawer__item-overlay" aria-hidden="true">
          <span
            class="cart-drawer__spinner"
            role="status"
            aria-label="Updating item"
          ></span>
        </div>

        ${image ? `
          <a
            class="cart-drawer__item-image-link"
            href="${escapeHtml(productUrl)}"
            tabindex="-1"
            aria-hidden="true"
          >
            <img
              class="cart-drawer__item-image"
              src="${escapeHtml(image)}"
              alt=""
              width="84"
              height="84"
              loading="lazy"
              decoding="async"
            >
          </a>
        ` : `
          <div
            class="cart-drawer__item-image cart-drawer__item-image--placeholder"
            aria-hidden="true"
          ></div>
        `}

        <div class="cart-drawer__item-details">
          <a
            class="cart-drawer__item-title"
            href="${escapeHtml(productUrl)}"
          >
            ${escapeHtml(title)}
          </a>

          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `
              <span class="cart-drawer__item-variant">
                ${escapeHtml(item.variant_title)}
              </span>
            `
            : ''}

          ${item.selling_plan_allocation?.selling_plan?.name
            ? `
              <span class="cart-drawer__item-variant">
                ${escapeHtml(item.selling_plan_allocation.selling_plan.name)}
              </span>
            `
            : ''}

          ${renderProperties(item.properties)}

          <span class="cart-drawer__item-price">
            ${hasDiscount ? `<s>${formatMoney(originalPrice)}</s> ` : ''}
            ${formatMoney(item.final_line_price)}
          </span>

          ${renderDiscounts(item.line_level_discount_allocations)}

          <div class="cart-drawer__item-controls">
            <button
              class="cart-drawer__qty-btn"
              type="button"
              data-action="decrease"
              aria-label="Decrease quantity for ${escapeHtml(title)}"
            >
              ${minusIcon || '<span aria-hidden="true">−</span>'}
            </button>

            <output
              class="cart-drawer__qty-value"
              aria-label="Quantity"
            >
              ${item.quantity}
            </output>

            <button
              class="cart-drawer__qty-btn"
              type="button"
              data-action="increase"
              aria-label="Increase quantity for ${escapeHtml(title)}"
            >
              ${plusIcon || '<span aria-hidden="true">+</span>'}
            </button>

            <button
              class="cart-drawer__item-remove"
              type="button"
              data-action="remove"
              aria-label="Remove ${escapeHtml(title)} from cart"
            >
              ${removeIcon || 'Remove'}
            </button>
          </div>

          <span
            class="cart-drawer__item-error-msg"
            aria-live="polite"
          ></span>
        </div>
      </li>
    `;
  }

  function render(currentCart) {
    if (!currentCart || !el.itemsList) return;

    cart = currentCart;

    const fragment = document.createDocumentFragment();

    currentCart.items.forEach((item) => {
      const template = document.createElement('template');

      template.innerHTML = itemMarkup(item).trim();

      fragment.appendChild(template.content);
    });

    el.itemsList.replaceChildren(fragment);

    updateEmptyState(currentCart);
    updateCartBadge(Number(currentCart.item_count || 0));
    updateFreeShipping(currentCart);
    notifyCartUpdated(currentCart);
  }

  async function parseError(response, fallback) {
    const data = await response.json().catch(() => null);

    return data?.description || data?.message || fallback;
  }

  async function fetchCart({ signal } = {}) {
    const response = await fetch(cartUrl('cart.js'), {
      headers: {
        Accept: 'application/json',
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(
        await parseError(response, 'Could not load your cart.')
      );
    }

    return response.json();
  }

  async function changeItem(key, quantity) {
    const response = await fetch(cartUrl('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity,
      }),
    });

    if (!response.ok) {
      throw new Error(
        await parseError(response, 'Could not update this item.')
      );
    }

    return response.json();
  }

  async function addItem(formData) {
    const response = await fetch(cartUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        await parseError(response, 'Could not add this item to the cart.')
      );
    }

    return response.json();
  }

  async function refresh({ loading = false } = {}) {
    requestController?.abort();
    requestController = new AbortController();

    if (loading) {
      setLoading(true);
    }

    try {
      const nextCart = await fetchCart({
        signal: requestController.signal,
      });

      render(nextCart);

      return nextCart;
    } finally {
      if (loading) {
        setLoading(false);
      }
    }
  }

  function open(trigger = document.activeElement) {
    if (isOpen) return;

    isOpen = true;
    opener = trigger instanceof HTMLElement ? trigger : null;

    el.drawer.classList.add('is-open');
    el.drawer.setAttribute('aria-hidden', 'false');

    el.overlay.classList.add('is-visible');
    el.overlay.setAttribute('aria-hidden', 'false');

    document.documentElement.classList.add('cart-drawer-open');

    window.setTimeout(() => {
      el.closeBtn?.focus();
    }, 0);
  }

  function close() {
    if (!isOpen) return;

    isOpen = false;

    el.drawer.classList.remove('is-open');
    el.drawer.setAttribute('aria-hidden', 'true');

    el.overlay.classList.remove('is-visible');
    el.overlay.setAttribute('aria-hidden', 'true');

    document.documentElement.classList.remove('cart-drawer-open');

    dismissError();
    opener?.focus?.();
  }

  function getFocusableElements() {
    return [
      ...el.drawer.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
    ].filter((node) => !node.hidden && node.offsetParent !== null);
  }

  function handleKeydown(event) {
    if (!isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async function handleItemControls(event) {
    const button = event.target.closest('[data-action]');

    if (!button || button.disabled) return;

    const itemElement = button.closest('.cart-drawer__item');
    const key = itemElement?.dataset.key;
    const quantityElement = itemElement?.querySelector(
      '.cart-drawer__qty-value'
    );

    if (!key || !quantityElement) return;

    const previousQuantity = Number(
      quantityElement.value || quantityElement.textContent
    );

    const action = button.dataset.action;

    const nextQuantity = action === 'increase'
      ? previousQuantity + 1
      : action === 'decrease'
        ? Math.max(0, previousQuantity - 1)
        : 0;

    if (nextQuantity === previousQuantity) return;

    const version = ++mutationVersion;

    clearItemError(itemElement);
    setItemUpdating(itemElement, true);

    try {
      const nextCart = await changeItem(key, nextQuantity);

      if (version !== mutationVersion) return;

      render(nextCart);

      setStatus(
        nextQuantity === 0
          ? 'Item removed from cart.'
          : 'Cart updated.'
      );
    } catch (error) {
      if (version !== mutationVersion) return;

      setItemUpdating(itemElement, false);

      showItemError(
        itemElement,
        error.message || 'Could not update this item. Please try again.'
      );

      showError(
        error.message || 'Could not update this item. Please try again.'
      );
    }
  }

  function isAddToCartForm(form) {
    const action = new URL(
      form.getAttribute('action') || '',
      window.location.origin
    )
      .pathname
      .replace(/\/$/, '');

    const rootCartAdd = `${root.replace(/\/$/, '')}/cart/add`
      .replace(/\/$/, '');

    return action === rootCartAdd || action === '/cart/add';
  }

  function interceptAddToCart() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('form');

      if (!form || !isAddToCartForm(form)) return;

      /*
        product-form.js owns forms with this attribute.
        Skipping them prevents duplicate /cart/add.js requests.
      */
      if (form.matches('[data-cart-add-form]')) return;

      /* Optional opt-out for forms that should not use the cart drawer. */
      if (form.dataset.cartDrawerIgnore === 'true') return;

      event.preventDefault();

      const submitter = event.submitter;

      if (submitter) {
        submitter.disabled = true;
      }

      dismissError();
      open(submitter || document.activeElement);
      setLoading(true);
      setStatus('Adding item to cart.');

      try {
        await addItem(new FormData(form));

        /*
          /cart/add.js commonly returns the newly added line item rather than
          the full cart. Fetch the complete cart before rendering the drawer.
        */
        const nextCart = await fetchCart();

        render(nextCart);
        setStatus('Item added to cart.');
      } catch (error) {
        showError(
          error.message || 'Could not add this item to the cart.'
        );

        setStatus('Could not add item to cart.');
      } finally {
        setLoading(false);

        if (submitter) {
          submitter.disabled = false;
        }
      }
    });
  }

  function initCartTrigger() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(
        '#cart-drawer-trigger, [data-cart-drawer-trigger]'
      );

      if (!trigger) return;

      event.preventDefault();

      dismissError();
      open(trigger);

      refresh({
        loading: !cart,
      }).catch((error) => {
        showError(error.message || 'Could not load your cart.');
      });
    });
  }

  function init() {
    Object.entries(SELECTORS).forEach(([key, selector]) => {
      el[key] = document.querySelector(selector);
    });

    if (!el.drawer || !el.overlay || !el.itemsList) return;

    el.closeBtn?.addEventListener('click', close);
    el.overlay.addEventListener('click', close);
    el.itemsList.addEventListener('click', handleItemControls);
    el.errorDismiss?.addEventListener('click', dismissError);

    el.checkoutBtn?.addEventListener('click', (event) => {
      if (el.checkoutBtn.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
      }
    });

    document.addEventListener('keydown', handleKeydown);

    interceptAddToCart();
    initCartTrigger();

    refresh().catch(() => {});
  }

  return {
    init,
    open,
    close,
    refresh,
    renderFromData: render,
    showError,
    setLoading,
    setStatus,
  };
})();

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    CartDrawer.init,
    { once: true }
  );
} else {
  CartDrawer.init();
}